/**
 * Patient portal design tokens — a web port of `patient-app/src/theme/tokens.ts`.
 *
 * Same language as the native app: warm ivory ground, white cards on hairlines,
 * deep aubergine as the confident colour, serif display type over a humanist
 * sans. Colour names are identical to the app's so a tenant palette resolves
 * the same way on both surfaces (see `brand.ts`). Every colour becomes a CSS
 * custom property on `<html>` — `surfaceLowest` → `--surface-lowest` — which
 * Tailwind and the stylesheet consume.
 */

export type ThemeName = 'light' | 'dark';

const lightPalette = {
  // The design's ground: a hair cooler than the app's paper so white cards
  // and the near-black rail both sit on it without either glaring.
  paper: '#F5F5F3',
  paperDeep: '#F1F0ED',
  white: '#FFFFFF',
  ink: '#17171A',
  inkSoft: '#2E2E35',
  // Desktop density lets secondary text sit lighter than on a phone; still
  // >= 4.5:1 on white at the 12.5px+ sizes it is used at.
  inkMuted: '#6B6B64',
  inkFaint: '#8C8C84',
  aubergine: '#2C2440',
  aubergineSoft: '#3A3055',
  violet: '#55467A',
  violetDeep: '#3B2F59',
  lavenderWash: '#EFECF5',
  lavenderWashSoft: '#F5F3FA',
  lavenderOnDark: '#C9C2DD',
  positive: '#3F6B53',
  positiveSoft: '#EAF0EB',
  caution: '#8F6524',
  cautionSoft: '#F7EFE2',
  critical: '#9D4436',
  criticalSoft: '#F9ECEA',
  // Solid hairlines (the design's #e6e5e0): alpha borders double up where
  // cards touch and read muddy against the paper.
  hairline: '#E6E5E0',
  hairlineStrong: '#DCDAD4',
  divider: '#F2F1EC',
  panel: '#FBFBFA',
  tableHead: '#FAF9F7',
  // Navigation rail: aubergine-black, its text and accent.
  rail: '#171522',
  railRaised: 'rgba(255, 255, 255, 0.09)',
  railText: '#FFFFFF',
  railTextMuted: '#A29BBD',
  railTextFaint: '#5D5779',
  railHairline: 'rgba(255, 255, 255, 0.07)',
};

export const lightColors = {
  surface: lightPalette.paper,
  surfaceLowest: lightPalette.white,
  surfaceLow: lightPalette.paperDeep,
  surfaceHigh: lightPalette.aubergine,
  surfaceGlass: 'rgba(252, 252, 251, 0.92)',
  inputSurface: lightPalette.paperDeep,

  onSurface: lightPalette.ink,
  onSurfaceStrong: lightPalette.inkSoft,
  onSurfaceVariant: lightPalette.inkMuted,
  onSurfaceFaint: lightPalette.inkFaint,
  onInverse: lightPalette.white,
  onInverseVariant: lightPalette.lavenderOnDark,

  primary: lightPalette.aubergine,
  primaryHover: lightPalette.aubergineSoft,
  primaryContainer: lightPalette.lavenderWash,
  primaryContainerSoft: lightPalette.lavenderWashSoft,
  onPrimary: lightPalette.white,

  accent: lightPalette.violet,
  accentDeep: lightPalette.violetDeep,

  success: lightPalette.positive,
  successContainer: lightPalette.positiveSoft,
  warning: lightPalette.caution,
  warningContainer: lightPalette.cautionSoft,
  error: lightPalette.critical,
  errorContainer: lightPalette.criticalSoft,
  info: lightPalette.violet,
  infoContainer: lightPalette.lavenderWash,

  border: lightPalette.hairline,
  borderStrong: lightPalette.hairlineStrong,
  /** Row separators inside a card: softer than the card's own border. */
  divider: lightPalette.divider,
  /** Secondary panels: top bar, master lists, drawers. */
  surfacePanel: lightPalette.panel,
  /** Table header rows. */
  surfaceHead: lightPalette.tableHead,
  rail: lightPalette.rail,
  railRaised: lightPalette.railRaised,
  railText: lightPalette.railText,
  railTextMuted: lightPalette.railTextMuted,
  railTextFaint: lightPalette.railTextFaint,
  railHairline: lightPalette.railHairline,
  railAccent: lightPalette.violet,
  onInverseHairline: 'rgba(255, 255, 255, 0.14)',
  onInverseFill: 'rgba(255, 255, 255, 0.12)',
  onInverseFillHover: 'rgba(255, 255, 255, 0.2)',

  scrim: 'rgba(20, 18, 28, 0.42)',
  skeleton: lightPalette.paperDeep,
  shadow: 'rgba(24, 20, 34, 0.08)',
};

const darkPalette = {
  base: '#131019',
  raised: '#1C1926',
  raisedHigh: '#272232',
  heroSurface: '#2A2440',
  heroSurfaceHover: '#332B4C',
  heroHairline: 'rgba(207, 197, 232, 0.22)',
  onHero: '#F6F4FA',
  onHeroVariant: '#B7ABD6',
  chalk: '#F1EFF6',
  chalkSoft: '#D5D1DF',
  chalkMuted: '#9A94A6',
  chalkFaint: '#6C6678',
  lavenderHigh: '#CFC5E8',
  lavenderHighHover: '#DDD5F0',
  onLavender: '#1B1629',
  lavenderAccent: '#B7ABD6',
  positive: '#7FC79E',
  positiveSoft: 'rgba(127, 199, 158, 0.14)',
  caution: '#E3B368',
  cautionSoft: 'rgba(227, 179, 104, 0.14)',
  critical: '#E8907F',
  criticalSoft: 'rgba(232, 144, 127, 0.15)',
  hairline: 'rgba(255, 255, 255, 0.10)',
  hairlineStrong: 'rgba(255, 255, 255, 0.18)',
  divider: 'rgba(255, 255, 255, 0.06)',
  panel: '#17141F',
  tableHead: '#1F1B29',
  rail: '#0F0D15',
  railRaised: 'rgba(255, 255, 255, 0.08)',
};

export const darkColors: typeof lightColors = {
  surface: darkPalette.base,
  surfaceLowest: darkPalette.raised,
  surfaceLow: darkPalette.raisedHigh,
  surfaceHigh: darkPalette.heroSurface,
  surfaceGlass: 'rgba(28, 25, 38, 0.92)',
  inputSurface: darkPalette.raisedHigh,

  onSurface: darkPalette.chalk,
  onSurfaceStrong: darkPalette.chalkSoft,
  onSurfaceVariant: darkPalette.chalkMuted,
  onSurfaceFaint: darkPalette.chalkFaint,
  onInverse: darkPalette.onHero,
  onInverseVariant: darkPalette.onHeroVariant,

  primary: darkPalette.lavenderHigh,
  primaryHover: darkPalette.lavenderHighHover,
  primaryContainer: darkPalette.raisedHigh,
  primaryContainerSoft: darkPalette.raised,
  onPrimary: darkPalette.onLavender,

  accent: darkPalette.lavenderAccent,
  accentDeep: darkPalette.lavenderHighHover,

  success: darkPalette.positive,
  successContainer: darkPalette.positiveSoft,
  warning: darkPalette.caution,
  warningContainer: darkPalette.cautionSoft,
  error: darkPalette.critical,
  errorContainer: darkPalette.criticalSoft,
  info: darkPalette.lavenderAccent,
  infoContainer: 'rgba(183, 171, 214, 0.14)',

  border: darkPalette.hairline,
  borderStrong: darkPalette.hairlineStrong,
  divider: darkPalette.divider,
  surfacePanel: darkPalette.panel,
  surfaceHead: darkPalette.tableHead,
  rail: darkPalette.rail,
  railRaised: darkPalette.railRaised,
  railText: darkPalette.chalk,
  railTextMuted: darkPalette.chalkMuted,
  railTextFaint: darkPalette.chalkFaint,
  railHairline: 'rgba(255, 255, 255, 0.07)',
  railAccent: darkPalette.lavenderAccent,
  onInverseHairline: darkPalette.heroHairline,
  onInverseFill: 'rgba(207, 197, 232, 0.12)',
  onInverseFillHover: 'rgba(207, 197, 232, 0.2)',

  scrim: 'rgba(0, 0, 0, 0.60)',
  skeleton: darkPalette.raisedHigh,
  shadow: 'rgba(0, 0, 0, 0.35)',
};

export type ThemeColors = typeof lightColors;
export type ColorToken = keyof ThemeColors;

/** `surfaceLowest` → `--surface-lowest`. */
export function cssVarName(token: string): string {
  return `--${token.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
}

/** The full colour set as `{ '--surface': '#…', … }`, ready for `style.setProperty`. */
export function colorsToCssVars(colors: ThemeColors): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(colors)) out[cssVarName(key)] = value;
  return out;
}

/** Latin font roles (CSS stacks). Swapped wholesale for Arabic — see fontPresets. */
export const latinFonts = {
  sans: "'Instrument Sans', 'Segoe UI', system-ui, -apple-system, sans-serif",
  serif: "'Instrument Serif', Georgia, 'Times New Roman', serif",
  /** Weight the serif display face is drawn at. Some presets have no serif and use a heavier sans. */
  serifWeight: '400',
};

export const arabicFonts: typeof latinFonts = {
  sans: "'IBM Plex Sans Arabic', 'Segoe UI', Tahoma, system-ui, sans-serif",
  serif: "'IBM Plex Sans Arabic', 'Segoe UI', Tahoma, system-ui, sans-serif",
  serifWeight: '600',
};

/** The face the elenthor wordmark is drawn in — Latin in every language. */
export const wordmarkFont = "'Manrope', 'Instrument Sans', system-ui, sans-serif";
